/* ==========================================================================
   Trust Me Aunty — theme behaviour
   Loaded with `defer` from layout/theme.liquid.

   Everything here is progressive enhancement: if this file fails to load, the
   page still renders and every control still works. No dependencies.

   Sections are re-rendered by the theme editor without a page reload, so each
   behaviour is bound with delegation or re-initialised on Shopify's
   section:load event rather than bound once at startup.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Scroll reveal
     Elements with .rv fade up once as they enter the viewport. With reduced
     motion they are simply shown immediately.
     ---------------------------------------------------------------------- */
  var observer = null;

  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.rv:not(.seen)');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) { el.classList.add('seen'); });
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('seen');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px' });
    }

    Array.prototype.forEach.call(targets, function (el) { observer.observe(el); });
  }

  /* ----------------------------------------------------------------------
     Accordions (product page: Ingredients, Directions, Aunty's tips)
     Markup: .acc > button[aria-expanded] + .acc-body
     Open state is the data-open attribute; the height animation is CSS
     (grid-template-rows 0fr -> 1fr), so nothing is measured in JS.
     ---------------------------------------------------------------------- */
  function toggleAccordion(button) {
    var acc = button.closest('.acc');
    if (!acc) return;
    var isOpen = acc.hasAttribute('data-open');
    if (isOpen) {
      acc.removeAttribute('data-open');
    } else {
      acc.setAttribute('data-open', '');
    }
    button.setAttribute('aria-expanded', String(!isOpen));
  }

  /* ----------------------------------------------------------------------
     Collection spice filters
     Pills carry data-f; cards carry data-spice. "all" shows everything.
     aria-pressed is kept in sync so screen readers hear the active filter.
     ---------------------------------------------------------------------- */
  function applyFilter(pill) {
    var group = pill.closest('[data-tma-filters]') || document;
    var value = pill.dataset.f;

    group.querySelectorAll('.pill').forEach(function (p) {
      var active = p === pill;
      p.classList.toggle('on', active);
      p.setAttribute('aria-pressed', String(active));
    });

    group.querySelectorAll('.pcard').forEach(function (card) {
      card.hidden = !(value === 'all' || card.dataset.spice === value);
    });
  }

  /* ----------------------------------------------------------------------
     Testimonial rail
     Arrows scroll the snap rail by roughly one screenful. Arrows hide
     themselves when there is nothing to scroll, so they are never decorative.
     ---------------------------------------------------------------------- */
  function railFor(button) {
    var row = button.closest('.qrow');
    return row ? row.querySelector('.qgrid') : null;
  }

  function syncArrows(rail) {
    var row = rail.closest('.qrow');
    if (!row) return;
    var overflowing = rail.scrollWidth > rail.clientWidth + 4;
    row.querySelectorAll('.carrow').forEach(function (b) {
      b.hidden = !overflowing;
    });
  }

  function initRails(root) {
    (root || document).querySelectorAll('.qgrid').forEach(function (rail) {
      syncArrows(rail);
      if (rail.dataset.tmaBound) return;
      rail.dataset.tmaBound = '1';
      rail.addEventListener('scroll', function () { syncArrows(rail); }, { passive: true });
    });
  }

  /* ----------------------------------------------------------------------
     Quantity stepper
     Buttons flank an <input type="number">; clamped to the input's own min.
     Dispatches `change` so Shopify's cart scripts see the new value.
     ---------------------------------------------------------------------- */
  function stepQuantity(button, direction) {
    var wrap = button.closest('.qty');
    if (!wrap) return;
    var input = wrap.querySelector('input');
    if (!input) return;
    var min = parseInt(input.min, 10) || 1;
    var next = (parseInt(input.value, 10) || min) + direction;
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ----------------------------------------------------------------------
     Delegated event handling
     One listener on the document covers every section, including ones the
     theme editor injects after load.
     ---------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var accBtn = event.target.closest('.acc > button');
    if (accBtn) { toggleAccordion(accBtn); return; }

    var pill = event.target.closest('.pill[data-f]');
    if (pill) { applyFilter(pill); return; }

    var arrow = event.target.closest('.carrow');
    if (arrow) {
      var rail = railFor(arrow);
      if (rail) {
        var dir = arrow.dataset.dir === 'prev' ? -1 : 1;
        rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      return;
    }

    var step = event.target.closest('.qty button[data-step]');
    if (step) { stepQuantity(step, step.dataset.step === 'up' ? 1 : -1); }
  });

  /* keyboard: space and enter already fire click on <button>, nothing extra needed */

  function init(root) {
    initReveal(root);
    initRails(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* theme editor re-renders a section without reloading the page */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll('.qgrid').forEach(syncArrows);
  }, { passive: true });
})();
